import { removeInitialContent } from "@escapio/shared/components/element-builder";
import { data } from "@escapio/shared/state/payload";
import { html, render } from "lit-html";
import { classMap } from "lit-html/directives/class-map.js";
import { repeat } from "lit-html/directives/repeat.js";
import connect from "../../../shared/data-store/connect";
import { nextFrame } from "../../../shared/event/next-frame";
import { checkVisibility } from "../../../shared/event/visibility-observer";
import { translate } from "../../../shared/l10n/translate";
import { NORMAL } from "../HotelHighlightStates";
import "../ads/ResultAdsBanner";
import "../ads/stroer/BannerForList";
import { format } from "../hotel/FilterToHashMapper";
import { addHash } from "../hotel/add-hash";
import { store } from "./DataStore";
import "./Hotel";
import { trackNoPrice } from "./tracker";
import { belongsEDLOnlyGroup } from "./sorting/SortForHotelList";
import { travelData } from "@escapio/portal/state/travelData";
import { hasDirectLink } from "@escapio/portal/state/DirectLink";

customElements.define(
	"esc-hotel-list",
	class extends connect(removeInitialContent(HTMLElement), store) {
		update({ filters, sorting, topHotelId }, { visiblePage, isLoading }) {
			const hotels = visiblePage;
			if (hotels.length > 0) {
				this.renderList(hotels, filters, sorting, topHotelId);
				if (
					hotels.length === 1 &&
					hotels[0].state.prices.hasLoadedWithoutOffers &&
					hasDirectLink(hotels[0])
				) {
					trackNoPrice("Liste nur EDL", filters);
				}
			} else if (!isLoading) {
				this.renderEmpty();
				trackNoPrice("Liste leer", filters);
			}
		}

		renderEmpty() {
			render(
				html` <div class="message message--colored message--top-space">
					<span
						class="fa fa-exclamation bullit bullit--blue message__icon"
					></span>
					<span>${translate("hotel_list_error_update_info")}</span>
				</div>`,
				this,
			);
		}

		renderList(elements, filters, sorting, topHotelId) {
			const hash = format(filters);

			// @TODO move to hotel widget and merge update methods
			let filteredHotelTypes = filters.hotelTypes;
			if (data.preselectedHotelType) {
				filteredHotelTypes = filteredHotelTypes.concat([
					data.preselectedHotelType,
				]);
			}

			const hotelCount = elements.length;

			const ADS = [
				{
					index: 3,
					ads: [
						{
							id: "result-ads",
							bannerElement: html`<esc-result-ads-banner></esc-result-ads-banner>`,
						},
					],
				},
				{
					index: 5,
					ads: this.showStroerAd
						? [
								{
									id: "stroer-tablet-top",
									bannerElement: html`<esc-stroer-banner-for-list
										slot-name="banner"
										class="hotel-list__ads-content-banner hotel-list__ads-content-banner--tablet"
									></esc-stroer-banner-for-list>`,
								},
								{
									id: "stroer-mobile-top",
									bannerElement: html`<esc-stroer-banner-for-list
										slot-name="topmobile"
										class="hotel-list__ads-content-banner hotel-list__ads-content-banner--mobile"
									></esc-stroer-banner-for-list>`,
								},
							]
						: [
								{
									id: "gpt-top",
									bannerElement: html`<esc-google-publisher-ad-container
										slot-id="gpt-leaderboard-1"
										slot-unit="list_leaderboard_1"
									></esc-google-publisher-ad-container>`,
								},
							],
				},
				this.showStroerAd
					? {
							index: 10,
							ads: [
								{
									id: "stroer-tablet-middle",
									bannerElement: html`<esc-stroer-banner-for-list
										slot-name="banner2"
										class="hotel-list__ads-content-banner hotel-list__ads-content-banner--tablet"
									></esc-stroer-banner-for-list>`,
								},
								{
									id: "stroer-mobile-middle",
									bannerElement: html`<esc-stroer-banner-for-list
										slot-name="topmobile2"
										class="hotel-list__ads-content-banner hotel-list__ads-content-banner--mobile"
									></esc-stroer-banner-for-list>`,
								},
							],
						}
					: null,
				{
					index: 15,
					ads: this.showStroerAd
						? [
								{
									id: "stroer-tablet-bottom",
									bannerElement: html`<esc-stroer-banner-for-list
										slot-name="banner3"
										class="hotel-list__ads-content-banner hotel-list__ads-content-banner--tablet"
									></esc-stroer-banner-for-list>`,
								},
								{
									id: "stroer-mobile-bottom",
									bannerElement: html`<esc-stroer-banner-for-list
										slot-name="topmobile3"
										class="hotel-list__ads-content-banner hotel-list__ads-content-banner--mobile"
									></esc-stroer-banner-for-list>`,
								},
							]
						: [
								{
									id: "gpt-bottom",
									bannerElement: html`<esc-google-publisher-ad-container
										slot-id="gpt-leaderboard-2"
										slot-unit="list_leaderboard_2"
									></esc-google-publisher-ad-container>`,
								},
							],
				},
			];

			ADS.filter(Boolean)
				.sort(({ index: a }, { index: b }) => Math.sign(b - a))
				.forEach(({ index, ads }) => {
					if (hotelCount > index) {
						elements.splice(index, 0, ...ads);
					}
				});

			const edlOnlyIndex = elements.findIndex(
				hotel =>
					!hotel.bannerElement &&
					belongsEDLOnlyGroup(sorting, topHotelId)(
						hotel,
						{ [hotel.id]: hotel.state.prices },
						travelData.hasDates,
					),
			);
			if (edlOnlyIndex > -1) {
				elements.splice(edlOnlyIndex, 0, {
					id: "hotels-edl-only",
					bannerElement: html`
						<div class="hotel-list-item hotels-group-header">
							<div>${translate("hotels_edl_only_heading")}</div>
						</div>
					`,
				});
			}

			const inTheNearIndex = elements.findIndex(
				({ distance }) => distance > -1,
			);
			if (inTheNearIndex > -1) {
				elements.splice(inTheNearIndex, 0, {
					id: "hotels-in-the-near",
					bannerElement: html`
						<div class="hotel-list-item hotels-group-header">
							<div>${translate("hotels_in_the_near_heading")}</div>
						</div>
					`,
				});
			}

			render(
				html`
					${repeat(
						elements,
						({ id }) => id,
						element => {
							if (element.bannerElement) {
								return element.bannerElement;
							}

							const classes = classMap({
								"hotel-list-item": true,
								"hotel-list-item--highlight": element.state.type !== NORMAL,
							});

							return html`<esc-hotel
								class=${classes}
								.hotel=${addHash(hash)(element)}
								.hotelTypeFilter=${filteredHotelTypes}
								.listSorting=${sorting}
							></esc-hotel>`;
						},
					)}
				`,
				this,
				{ host: this },
			);

			nextFrame().then(checkVisibility);
		}

		get showStroerAd() {
			return this.hasAttribute("show-stroer-ad");
		}
	},
);
